import { ReactElement, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Allotment } from 'allotment'
import { find, reject } from 'lodash-es'
import { Box, Button } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { publishSaveEvent } from '@ui/events'
import { useSnackbar } from '@ui/snackbar'
import { Drawer, LayoutDrawer } from '@ui/structure'
import { useEnterEditorPage } from '@editorHooks'
import { ViewerRef, PDFAConformance, Viewer } from '@editorComponents'
import { getPlans } from '@queries'
import PlanInformationSection from './components/PlanInformationSection'
import PlansSection from './components/PlansSection'
import usePlansQuery from './hooks/usePlansQuery'

const emptyArray: Plan[] = []

const Plans = (): ReactElement => {
  useEnterEditorPage('plans')

  const { projectId } = useParams()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)

  const { data: plans = emptyArray, isLoading: isLoadingPlans } = usePlansQuery()

  const viewerRef = useRef<ViewerRef | null>(null)

  const queryClient = useQueryClient()

  const onSelectPlan = (guid: string | null) => {
    if (selectedPlan?.guid === guid) return

    if (!guid) {
      setSelectedPlan(null)
      return
    }

    const plan = find(plans, { guid })!

    // prevent saving when plan is changed
    if (selectedPlan?.local_isChanged) {
      const key = `plan-changed-warning-${guid}`
      enqueueSnackbar('Plan hat ungespeicherte Änderungen', {
        key,
        variant: 'warning',
        action: key => [
          <Button
            key="change-plan"
            color="inherit"
            onClick={() => {
              setSelectedPlan(plan)
              closeSnackbar(key)
            }}
            sx={{ textTransform: 'none' }}
          >
            Trotzdem wechseln
          </Button>,
          <Button
            key="close"
            color="inherit"
            onClick={() => {
              closeSnackbar(key)
            }}
            sx={{ textTransform: 'none' }}
          >
            Schließen
          </Button>,
        ],
      })
      return
    }

    setSelectedPlan(plan)
  }

  const onAddPlan = (plan: Plan) => {
    const queryData = queryClient.getQueryData(getPlans.getKey(projectId as string)) as Plan[]
    queryClient.setQueryData(getPlans.getKey(projectId as string), [...(queryData || []), plan])

    setSelectedPlan(plan)
  }

  const onDeletePlan = (deletedPlan: Plan) => {
    setSelectedPlan(null)
    const queryData = queryClient.getQueryData(getPlans.getKey(projectId as string)) as Plan[]
    const filteredPlans = reject(queryData, plan => plan.guid === deletedPlan.guid)
    queryClient.setQueryData(getPlans.getKey(projectId as string), filteredPlans)

    setSelectedPlan(null)
  }

  const onUpdatePlan = (updatedPlan: Plan) => {
    const queryData = queryClient.getQueryData(getPlans.getKey(projectId as string)) as Plan[]
    const updatedPlans = queryData.map(plan =>
      plan.guid === updatedPlan.guid ? updatedPlan : plan,
    )
    queryClient.setQueryData(getPlans.getKey(projectId as string), updatedPlans)

    setSelectedPlan({ ...updatedPlan, local_isChanged: false })
  }

  const plansForDatagrid = useMemo(() => {
    if (!selectedPlan) return plans

    const index = plans.findIndex(plan => plan.guid === selectedPlan.guid)
    if (index === -1) return plans

    // Replace the plan at the found index with selectedPlan
    return [...plans.slice(0, index), selectedPlan, ...plans.slice(index + 1)]
  }, [plans, selectedPlan])

  return (
    <LayoutDrawer fullHeight>
      <Box
        sx={{
          paddingLeft: 5,
          flexGrow: 1,
        }}
      >
        <Viewer
          ref={viewerRef}
          viewerHeight="calc(100vh - 58px)"
          additionalShortcuts={[
            {
              key: 's',
              action: () => publishSaveEvent({ type: 'save' }),
            },
          ]}
          document={
            selectedPlan
              ? {
                  guid: selectedPlan.guid,
                  name: selectedPlan.node_name,
                  url: selectedPlan.url as string,
                  fileType: 'application/pdf',
                }
              : null
          }
          onChangeDocument={() => {
            setSelectedPlan(prev => (prev !== null ? { ...prev, local_isChanged: true } : null))
          }}
        />
      </Box>

      <Drawer variant="persistent">
        <Allotment vertical>
          <PlansSection
            selectedPlan={selectedPlan?.guid || null}
            setSelectedPlan={onSelectPlan}
            onAddPlan={onAddPlan}
            onDeletePlan={onDeletePlan}
            plans={plansForDatagrid}
            isLoadingPlans={isLoadingPlans}
            getBlobPDFA={async (pdfUlr: string, conformance: PDFAConformance) => {
              if (viewerRef.current) {
                return viewerRef.current.getBlobPDFA(pdfUlr, conformance)
              }
              return null
            }}
          />

          <Allotment.Pane preferredSize="30%">
            <PlanInformationSection
              selectedPlan={selectedPlan || null}
              onUpdatePlan={onUpdatePlan}
              getCurrentDocument={async () => {
                if (viewerRef.current) {
                  return viewerRef.current.getBlobCurrentDocument()
                }
                return null
              }}
              enableSave={!selectedPlan?.local_isChanged}
            />
          </Allotment.Pane>
        </Allotment>
      </Drawer>
    </LayoutDrawer>
  )
}

export default Plans
